import CFG from '../gameLoopConfig.json';
import {
  ENEMY_SIZE,
  ATTACK_RADIUS, MAX_ATTACK_RADIUS,
  TIER_COLORS,
} from '../../../constants';
import { rollDrop, rollGold, rollRuneDrop, rollChestItem, WEAPON_ATTACK_PROFILES } from '../../../data/items';
import { spawnGroundDrop } from '../helpers';

const IS_TOUCH = navigator.maxTouchPoints > 0;

export function checkAutoAttack(s, timestamp, setAttackRenderKey) {
  if (!IS_TOUCH || !s.alive || s.attack.active) return;
  const ap  = WEAPON_ATTACK_PROFILES[s.weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
  const acd = Math.max(250, ap.baseCd * (s.cdMult ?? 1) * (s.activeModifier?.playerCdMult ?? 1));
  if (timestamp - (s.lastAttackTime ?? -acd) >= acd) {
    s.attack         = { active: true, startTime: timestamp, damageDealt: false };
    s.lastAttackTime = timestamp;
    setAttackRenderKey(timestamp);
  }
}

export function processMeleeAttack(s, timestamp, pcx, pcy, setAttackRenderKey, setChestLootOptions, setGameState) {
  if (!s.attack.active) return false;

  const attackProfile = WEAPON_ATTACK_PROFILES[s.weaponType ?? 'unarmed'] ?? WEAPON_ATTACK_PROFILES.unarmed;
  if (timestamp - s.attack.startTime >= attackProfile.duration) {
    s.attack.active = false;
    setAttackRenderKey(0);
    return false;
  }
  if (s.attack.damageDealt) return false;

  s.attack.damageDealt = true;
  const mobileMult = IS_TOUCH ? 0.5 : 1;
  const r = Math.min(MAX_ATTACK_RADIUS, ATTACK_RADIUS * s.rangeMult * (s.activeModifier?.playerRangeMult ?? 1) * attackProfile.radiusFactor * mobileMult);
  let kills = 0;
  let bossKillsThisSwing = 0;
  const surviving = [];
  let ftSeq = 0;
  let tierBossKilled = false;
  let tierBossLoot   = null;

  // Solar Flare: ×5 damage on first attack after level-up
  const isSolarFlare = s.solarFlareReady;
  if (isSolarFlare) {
    s.solarFlareReady = false;
    s.spellEffects.push({ id: `se_sf_${timestamp}`, type: 'explosion', x: pcx, y: pcy, radius: r + 50, startTime: timestamp, duration: 500 });
  }
  const effectiveAttackDmg = Math.max(1, s.attackDamage + (s.activeModifier?.playerDmgFlatMod ?? 0));
  const baseDamage = isSolarFlare
    ? effectiveAttackDmg * 5
    : Math.ceil(effectiveAttackDmg * (s.damageMult ?? 1));

  for (const enemy of s.enemies) {
    const eSize = enemy.size ?? ENEMY_SIZE;
    const ex    = enemy.x + eSize / 2;
    const ey    = enemy.y + eSize / 2;
    const cx    = Math.max(enemy.x, Math.min(pcx, enemy.x + eSize));
    const cy    = Math.max(enemy.y, Math.min(pcy, enemy.y + eSize));
    const dx    = pcx - cx;
    const dy    = pcy - cy;

    if (dx * dx + dy * dy <= r * r) {
      const isCrit      = !isSolarFlare && Math.random() < (s.critRate ?? 0.05);
      const critScaled  = isCrit ? Math.round(baseDamage * (s.critDamage ?? 1.5)) : baseDamage;
      const finalDamage = Math.max(1, critScaled - (enemy.armor ?? 0));
      enemy.hp -= finalDamage;

      if (enemy.hp <= 0) {
        kills++;
        if (enemy.isBoss) bossKillsThisSwing++;

        if (enemy.isTierBoss) {
          s.tierBossAlive = false;
          tierBossKilled  = true;
          const ENVS   = ['forest', 'desert', 'snow', 'castle', 'water', 'graveyard', 'inferno'];
          const others = ENVS.filter(e => e !== (s.environment ?? 'forest'));
          s.environment = others[Math.floor(Math.random() * others.length)];
          const tbLevel   = (s.score ?? 0) / 1000;
          tierBossLoot    = [rollChestItem(tbLevel), rollChestItem(tbLevel), rollChestItem(tbLevel)];
          const tb = CFG.tierBoss;
          for (let xi = 0; xi < tb.xpDropCount; xi++) {
            const angle = (xi / tb.xpDropCount) * Math.PI * 2;
            spawnGroundDrop(s, 'xp', ex + Math.cos(angle) * tb.xpDropRadius - 10, ey + Math.sin(angle) * tb.xpDropRadius - 10, 24);
          }
          s.spellEffects.push({ id: `se_tbkill_${timestamp}`, type: 'kill_explosion', x: ex, y: ey, color: '#ff6600', isBoss: true, startTime: timestamp, duration: 1400 });
          continue;
        }

        const dropExtra   = s.activeModifier?.dropChanceBonus ?? 0;
        const playerLevel = (s.score ?? 0) / 1000;
        const magicFind   = s.magicFind ?? 0;
        const drop = rollDrop(dropExtra, playerLevel, magicFind, s.currentTier ?? 0);
        if (drop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, drop);
        const runeDrop = rollRuneDrop(dropExtra, (enemy.isBoss ? s.currentTier * 2 : enemy.tierIdx * 2), magicFind);
        if (runeDrop) spawnGroundDrop(s, 'item', ex + (Math.random() - 0.5) * 14 - 12, ey + (Math.random() - 0.5) * 14 - 12, 24, runeDrop);
        if (Math.random() < 0.5) spawnGroundDrop(s, 'xp', ex + (Math.random() - 0.5) * 20 - 8, ey + (Math.random() - 0.5) * 20 - 8, 24);

        const goldBase = rollGold(enemy.isBoss, s.goldFindBonus ?? 0);
        const gold = goldBase > 0 ? Math.round(goldBase * (s.activeModifier?.goldMult ?? 1)) : 0;
        if (gold > 0) {
          s.goldThisRun = (s.goldThisRun ?? 0) + gold;
          s.floatingTexts.push({ id: `ft_g_${timestamp}_${ftSeq++}`, x: ex, y: ey - 20, text: `+${gold}💰`, color: '#ffd700', startTime: timestamp, duration: 1100 });
        }

        if (s.soulDrainActive) {
          s.soulDrainCounter = (s.soulDrainCounter ?? 0) + 1;
          if (s.soulDrainCounter >= CFG.combat.soulDrainKillsNeeded) {
            s.soulDrainCounter = 0;
            if (s.hp < (s.maxHp ?? 100)) {
              s.hp = Math.min(s.maxHp ?? 100, s.hp + CFG.combat.soulDrainHealAmount);
              s.floatingTexts.push({ id: `ft_heal_${timestamp}_${ftSeq++}`, x: pcx, y: pcy - 30, text: `❤️ +${CFG.combat.soulDrainHealAmount}`, color: '#ff4d6d', startTime: timestamp, duration: 1200 });
            }
          }
        }
        if (s.bloodlustActive) s.attackDamage = Math.round((s.attackDamage + 0.1) * 10) / 10;
        if (s.vampiricActive && s.hp < (s.maxHp ?? 100)) s.hp = Math.min(s.maxHp ?? 100, s.hp + CFG.combat.vampiricHealPerKill);

        s.floatingTexts.push({ id: `ft_d_${timestamp}_${ftSeq++}`, x: ex, y: ey, text: isSolarFlare ? `☀️${finalDamage}` : isCrit ? `⚡${finalDamage}` : `${finalDamage}`, color: isSolarFlare ? '#ff6600' : isCrit ? '#ffdd00' : '#ff6600', startTime: timestamp, duration: 700 });
        if (s.spellEffects.length < CFG.spellFxCap) {
          const expColor = enemy.isBoss ? '#ffaa00' : enemy.isElite ? '#ffc832' : (TIER_COLORS[enemy.tierIdx % TIER_COLORS.length]?.bg ?? '#ff4444');
          s.spellEffects.push({ id: `se_kx_${timestamp}_${ftSeq}`, type: 'kill_explosion', x: ex, y: ey, color: expColor, isBoss: !!(enemy.isBoss), startTime: timestamp, duration: enemy.isBoss ? 800 : 450 });
        }
        continue;
      }

      s.floatingTexts.push({ id: `ft_d_${timestamp}_${ftSeq++}`, x: ex, y: ey, text: isCrit ? `⚡${finalDamage}` : `${finalDamage}`, color: isCrit ? '#ffdd00' : '#ff4444', startTime: timestamp, duration: 700 });
    }
    surviving.push(enemy);
  }

  // Chain Reaction (Stormsurge Band)
  if (s.stormSurgeActive && kills > 0) {
    const chainCount = kills;
    for (let ci = 0; ci < chainCount; ci++) {
      if (Math.random() < 0.3 && surviving.length > 0) {
        let nearestIdx = 0, nearestDist = Infinity;
        for (let j = 0; j < surviving.length; j++) {
          const e = surviving[j];
          const d = Math.hypot(e.x + (e.size ?? ENEMY_SIZE) / 2 - pcx, e.y + (e.size ?? ENEMY_SIZE) / 2 - pcy);
          if (d < nearestDist) { nearestDist = d; nearestIdx = j; }
        }
        const [chained] = surviving.splice(nearestIdx, 1);
        kills++;
        const cex = chained.x + (chained.size ?? ENEMY_SIZE) / 2;
        const cey = chained.y + (chained.size ?? ENEMY_SIZE) / 2;
        const cDrop = rollDrop(0, (s.score ?? 0) / 1000, 0, s.currentTier ?? 0);
        if (cDrop) spawnGroundDrop(s, 'item', cex - 12, cey - 12, 24, cDrop);
        const cRuneDrop = rollRuneDrop(0, chained.tierIdx * 2);
        if (cRuneDrop) spawnGroundDrop(s, 'item', cex - 12, cey - 12, 24, cRuneDrop);
        if (Math.random() < 0.5) spawnGroundDrop(s, 'xp', cex - 8, cey - 8, 24);
        const cGold = rollGold(chained.isBoss, s.goldFindBonus ?? 0);
        if (cGold > 0) s.goldThisRun = (s.goldThisRun ?? 0) + cGold;
        const cExpColor = chained.isBoss ? '#ffaa00' : chained.isElite ? '#ffc832' : (TIER_COLORS[chained.tierIdx % TIER_COLORS.length]?.bg ?? '#ff4444');
        s.spellEffects.push({ id: `se_ckx_${timestamp}_${ci}`, type: 'kill_explosion', x: cex, y: cey, color: cExpColor, isBoss: !!(chained.isBoss), startTime: timestamp + ci * 60, duration: 450 });
        s.spellEffects.push({ id: `se_sr_${timestamp}_${ci}`, type: 'lightning', x: cex, y: cey, startTime: timestamp + ci * 60, duration: 350 });
        s.floatingTexts.push({ id: `ft_chain_${timestamp}_${ci}`, x: cex, y: chained.y, text: '⚡', color: '#88ccff', startTime: timestamp, duration: 600 });
      }
    }
  }

  s.enemies = surviving;
  s.kills   = (s.kills ?? 0) + kills;
  const tierXpBonus = Math.floor((s.currentTier ?? 0) / 10) * 10;
  s.xp     += kills * (s.xpPerKill + tierXpBonus) * (s.activeModifier?.xpMult ?? 1);
  if (bossKillsThisSwing > 0) s.bossKills = (s.bossKills ?? 0) + bossKillsThisSwing;

  if (tierBossKilled && tierBossLoot) {
    s.isTierBossChest = true;
    setChestLootOptions(tierBossLoot);
    setGameState('chestopen');
    return true;
  }
  return false;
}
